/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CognitoUser } from 'src/app/components/authentication/cognito-model/cognito-user.model';
import { VMOnlineApiService } from 'src/app/vmonline-api.service';
import { Router } from '@angular/router';
import { AuthService } from '../../../auth/auth.service';
import { VMOnlineApiEndpoints } from 'src/app/vmonline-api-endpoints';
import { LocalRoutes } from 'src/app/local-routes';
import { Challenges } from './challenges';
import { State } from 'src/app/state/app.state';
import { Store } from '@ngrx/store';
import * as loginAction from '../store/actions/login.action';

@Injectable({
	providedIn: 'root',
})

/** Handles logging into VM Online after receiving a token from the SSO provider
 * (either Cognito or Azure AD at the time of writing.) */
export class VMOnlineLoginService {
	private http = inject(HttpClient);
	private vmOnlineApi = inject(VMOnlineApiService);
	private router = inject(Router);
	private authService = inject(AuthService);
	private store = inject<
		Store<{
			login: State;
		}>
	>(Store);

	/** The cognito user accessible during the authentication flow */
	cognitoUser: CognitoUser;
	setupTotpCode: string = null;

	readonly ERROR_GENERAL_LOGIN = 'Something went wrong. Please contact support or try again later';
	readonly ERROR_UNAUTHORIZED = 'Access to VM Online is not authorized for this user';

	constructor() {}

	/** Gets whether or not the site allows retrieval of passwords from the login screen. */
	allowPasswordRetrieval(): Observable<boolean> {
		return this.http.get<boolean>(this.vmOnlineApi.authUrl(VMOnlineApiEndpoints.AUTH_ALLOW_PASSWORD_RETRIEVAL));
	}

	/** Sign-in to VM Online using an authenticated Cognito user */
	signInWithCognitoUser(user: CognitoUser): Observable<any> {
		// TODO Convert to using rxJs maps instead of nested subscriptions,
		// and remove use of 'any' in the Observable response.

		return new Observable((observer) => {
			if (!user.signInUserSession) {
				observer.next({ error: this.ERROR_GENERAL_LOGIN });
				return;
			}

			this.signin(user.signInUserSession.idToken.jwtToken).subscribe(
				(response) => {
					if (response?.challengeName === Challenges.CHALLENGE_NAME_MFA_SETUP) {
						observer.next({ mfaSetupRequired: true });
						return;
					}

					// Do not allow further sign-ins using the Cognito tokens
					this.authService.signOutOfCognito();

					this.authService.clearCachedCurrentUser();

					this.getAntiForgeryCookie().subscribe(
						() => {
							// updating the state of the user
							this.store.dispatch(
								loginAction.userLoggedIn({
									isUserLoggedIn: true,
								})
							);

							this.doRedirectAfterLogin();

							observer.next({ success: true });
						},
						() => observer.next({ error: this.ERROR_GENERAL_LOGIN })
					);
				},
				(error) => {
					if (error.status && error.status === 401) {
						observer.next({ error: this.ERROR_UNAUTHORIZED });
					} else {
						observer.next({ error: this.ERROR_GENERAL_LOGIN });
					}
				}
			);
		});
	}

	/** Calls the sign-in endpoint on VM Online with a JWT token supplied by
	 * Cognito (or other SSO provider) */

	private signin(jwtToken: string): Observable<any> {
		return this.http.post<any>(this.vmOnlineApi.authUrl(VMOnlineApiEndpoints.AUTH_SIGN_IN), {
			jwtToken,
		});
	}

	/** Gets the CSRF anti-forgery cookie from VM Online */
	private getAntiForgeryCookie(): Observable<any> {
		return this.http.get(this.vmOnlineApi.authUrl(VMOnlineApiEndpoints.AUTH_ANTIFORGERY));
	}

	/** Redirect to the page the user was originally trying to go to, or
	 * otherwise the appropriate default page */
	private doRedirectAfterLogin(): void {
		// Either redirect to a full url, or to a route. Full URLs must be sanity checked.
		const returnUrl = this.authService.returnToUrlAfterLogin;
		const returnRoute = this.authService.returnToRouteAfterLogin;

		if (returnUrl) {
			if (this.canRedirectToReturnUrl(returnUrl, document.location.origin)) {
				document.location.href = this.authService.returnToUrlAfterLogin;
			} else {
				console.error(`Redirect to URL ${returnUrl} is disallowed.`);
				this.router.navigateByUrl(LocalRoutes.START_PAGE_DEFAULT);
			}
		} else if (returnRoute) {
			// Confusingly, this is redirecting to the route, not the full URL
			this.router.navigateByUrl(returnRoute);
		} else {
			this.authService.getCurrentUser().subscribe(() => {
				this.router.navigateByUrl(LocalRoutes.START_PAGE_DEFAULT);
			});
		}
	}

	/** Returns true if the url is local (relative) to the current host */
	public canRedirectToReturnUrl(url: string, currentOrigin: string): boolean {
		// Do not redirect to a different domain, as this is consider a security vulnerability
		// Note that if we move login on to a separate domain to regular VM Online, the logic
		// here will need to change
		const isLocalToHost =
			url.length > 0 && url[0] === '/' && (url.length === 1 || (url[1] !== '/' && url[1] !== '\\'));

		if (isLocalToHost) return true;
		try {
			const uri = new URL(url);
			if (uri.origin === currentOrigin) return true;
			// Allow localhost
			return uri.origin === 'http://localhost:4500';
		} catch {
			return false;
		}
	}
}
